import { addGamepad, Stream } from "@blacknut/javascript-sdk/dist";
import { logD, logE } from "@blacknut/logging/dist";

import {
  BNKeyboardEvent,
  BNTouchEvent,
  GamepadAxisEvent,
  GamepadButtonEvent,
  GamepadConnectEvent,
  GamepadTriggerEvent,
  InputProvider,
  MouseClickEvent,
  PlayerWebRTC,
} from "@blacknut/playerwebrtc";
import { Subject } from "rxjs";
import { parseProductIdVendorId } from "../../utils/gamepad";
import { LOGGING_TAG } from "../../utils/logging";

class GamepadInputProvider implements InputProvider {
  private player: PlayerWebRTC;
  private stream: Stream;
  private paused = false;
  constructor(player: PlayerWebRTC, stream: Stream) {
    this.player = player;
    this.stream = stream;
  }

  private gamepadSet = new Set<string>();

  //Pending connect events. Connect can be sent before inputs are created
  //Store connect event if this case and deliver it the next time a gamepad event occurs
  private pendingGamepadConnectEvents: GamepadConnectEvent[] = [];

  private _onGamepadPreConnect = new Subject<GamepadConnectEvent>();
  private _onGamepadPostDisconnect = new Subject<GamepadConnectEvent>();

  private connectIfNeeded(player: number) {
    const e = this.pendingGamepadConnectEvents.find((e) => e.index === player);
    if (e) {
      this.onGamepadConnect(e);
    }
  }

  /** Called right before open is sent (in order to close previously created socket for player 0) */
  public onGamepadPreEvent() {
    return this._onGamepadPreConnect.asObservable();
  }

  /** Called right after close is sent (in order to reopen if ndeded a socket for player 0) */
  public onGamepadPostEvent() {
    return this._onGamepadPostDisconnect.asObservable();
  }
  public teardown() {
    // TODO Should close be sent ??
    this._onGamepadPreConnect = new Subject<GamepadConnectEvent>();
    this._onGamepadPostDisconnect = new Subject<GamepadConnectEvent>();
    this.pendingGamepadConnectEvents = [];
    this.gamepadSet.clear();
  }

  onGamepadConnect(e: GamepadConnectEvent) {
    logD(
      LOGGING_TAG,
      "On gamepad connect %o",
      e,
      this.player.WebRTCClient.inputListener,
    );
    if (this.player.WebRTCClient.inputListener) {
      this._onGamepadPreConnect.next(e);

      if (!this.player.WebRTCClient.inputListener.onGamepadConnect(e)) {
        // Inputs created but not ready yet
        this.pendingGamepadConnectEvents.push(e);
        return false;
      }

      //Remove it if it was previously pushed
      this.pendingGamepadConnectEvents = this.pendingGamepadConnectEvents.filter(
        (curr) => curr.index !== e.index,
      );

      if (e.id) {
        const { productId, vendorId } = parseProductIdVendorId(e.id);
        const _id = `${productId} - ${vendorId}`;
        if (productId && vendorId && this.stream && !this.gamepadSet.has(_id)) {
          this.gamepadSet.add(_id);
          logD(LOGGING_TAG, "New gamepad %o %o(%o)", e.id, productId, vendorId);
          addGamepad(this.stream.id, e.id, `${productId}̀`, `${vendorId}`).subscribe({
            next: () => {
              logD(LOGGING_TAG, "Gamepad added");
            },
            error: (e: unknown) => {
              logE(LOGGING_TAG, "Caught error adding gamepad", e);
            },
          });
        }
      }

      return true;
    } else {
      logD(LOGGING_TAG, "Inputs not created yet, postpone");
      // Inputs not created yet. This occurs when a gamepad event is first sent but input socket is created after play button has been clicked
      this.pendingGamepadConnectEvents.push(e);
      return false;
    }
  }
  onGamepadDisconnect(e: GamepadConnectEvent) {
    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onGamepadDisconnect(e);
      this.pendingGamepadConnectEvents = this.pendingGamepadConnectEvents.filter(
        (curr) => curr.index !== e.index,
      );
    }
  }
  onGamepadButtonEvent(e: GamepadButtonEvent) {
    if (this.paused) return;

    this.connectIfNeeded(e.player);
    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onGamepadButtonEvent(e);
    }
  }
  onGamepadBackEvent(e: GamepadButtonEvent) {
    if (this.paused) return;

    this.connectIfNeeded(e.player);

    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onGamepadBackEvent(e);
    }
  }
  onGamepadTrigger(e: GamepadTriggerEvent) {
    if (this.paused) return;

    this.connectIfNeeded(e.player);

    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onGamepadTrigger(e);
    }
  }
  onGamepadAxis(e: GamepadAxisEvent) {
    if (this.paused) return;
    this.connectIfNeeded(e.player);

    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onGamepadAxis(e);
    }
  }
  onMouseClick(e: MouseClickEvent) {
    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onMouseClick(e);
    }
  }
  onKeyboardEvent(e: BNKeyboardEvent) {
    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onKeyboardEvent(e);
    }
  }

  onTouchEvent(e: BNTouchEvent) {
    if (this.player.WebRTCClient.inputListener) {
      this.player.WebRTCClient.inputListener.onTouchEvent(e);
    }
  }

  pause() {
    this.paused = true;
    this.player.unBind();
  }
  resume() {
    this.paused = false;
    this.player.bind();
  }
}

export default GamepadInputProvider;
